import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { InputText } from 'primereact/inputtext';
import { Avatar } from 'primereact/avatar';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Enrollment {
  is_enrolled: boolean;
  course_id: string | null;
  enrollment_no: string | null;
  course_name: string | null;
  course_title: string | null;
  batch_id: string | null;
  batch_name: string | null;
  batch_time: string | null;
  start_month: string | null;
  start_year: number | null;
  end_month: string | null;
  end_year: number | null;
  date_of_admission: string | null;
}

interface Student {
  student_id: string;
  student_custom_id: string | null;
  name: string;
  gender: string | null;
  dateOfBirth: string | null;
  mobileNumber: string | null;
  email: string | null;
  category: string | null;
  district: string | null;
  state: string | null;
  status: string;
  profilePicture: { cdn_url?: string; dev_url?: string } | null;
  enrollment: Enrollment;
  is_registered: boolean;
  created_at: string | null;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface BranchStudentsProps {
  branchId: string;
  instituteId: string;
  branchName: string;
  onBack: () => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const getInitials = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');

const formatDOB = (iso: string | null) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
};

const GENDER_COLORS: Record<string, { bg: string; color: string }> = {
  male:   { bg: '#e3f2fd', color: '#1565c0' },
  female: { bg: '#fce4ec', color: '#880e4f' },
  other:  { bg: '#f3e5f5', color: '#4a148c' },
};

// ── Skeleton row ──────────────────────────────────────────────────────────────

const SkeletonRow: React.FC = () => (
  <div style={{
    display: 'grid', gridTemplateColumns: '48px 1fr 140px 140px 120px 100px 80px',
    gap: '16px', alignItems: 'center', padding: '16px 20px',
    borderBottom: '1px solid #f5f5f5',
  }}>
    {[48, 180, 120, 120, 90, 80, 60].map((w, i) => (
      <div key={i} style={{
        height: i === 0 ? 48 : 14, width: i === 0 ? 48 : `${Math.min(w, 100)}%`,
        borderRadius: i === 0 ? '50%' : 6,
        background: 'linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%)',
        backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite',
      }} />
    ))}
    <style>{`@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
  </div>
);

// ── Student row ───────────────────────────────────────────────────────────────

const StudentRow: React.FC<{ student: Student; index: number }> = ({ student, index }) => {
  const avatarUrl = student.profilePicture?.cdn_url || student.profilePicture?.dev_url;
  const genderStyle = GENDER_COLORS[student.gender?.toLowerCase() ?? ''] ?? { bg: '#f5f5f5', color: '#666' };
  const enrolled = student.enrollment?.is_enrolled;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '48px 1fr 160px 160px 120px 100px 90px',
        gap: '16px', alignItems: 'center',
        padding: '14px 20px',
        borderBottom: '1px solid #f9f9f9',
        background: index % 2 === 0 ? 'white' : '#fafafa',
        transition: 'background 0.15s ease',
      }}
      onMouseEnter={e => (e.currentTarget.style.background = '#f0f7f4')}
      onMouseLeave={e => (e.currentTarget.style.background = index % 2 === 0 ? 'white' : '#fafafa')}
    >
      {/* Avatar */}
      {avatarUrl ? (
        <img src={avatarUrl} alt={student.name}
          style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', border: '2px solid #e0e0e0' }} />
      ) : (
        <Avatar
          label={getInitials(student.name)}
          style={{ width: 40, height: 40, background: '#0f3d3e', color: '#d4f17e', fontSize: '0.8rem', fontWeight: '700', flexShrink: 0 }}
          shape="circle"
        />
      )}

      {/* Name + ID */}
      <div style={{ minWidth: 0 }}>
        <p style={{ margin: '0 0 2px 0', fontSize: '0.9rem', fontWeight: '700', color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {student.name}
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          {student.student_custom_id && (
            <span style={{ fontSize: '0.68rem', fontFamily: 'monospace', color: '#0f3d3e', fontWeight: '700', background: '#e8f5e9', padding: '1px 6px', borderRadius: '4px' }}>
              {student.student_custom_id}
            </span>
          )}
          {student.mobileNumber && (
            <span style={{ fontSize: '0.7rem', color: '#888' }}>
              <i className="pi pi-phone" style={{ fontSize: '0.6rem', marginRight: '3px' }} />
              {student.mobileNumber}
            </span>
          )}
        </div>
      </div>

      {/* Course */}
      <div style={{ minWidth: 0 }}>
        {enrolled && student.enrollment.course_name ? (
          <>
            <p style={{ margin: '0 0 2px 0', fontSize: '0.8rem', fontWeight: '600', color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {student.enrollment.course_name}
            </p>
            {student.enrollment.enrollment_no && (
              <p style={{ margin: 0, fontSize: '0.65rem', fontFamily: 'monospace', color: '#666' }}>
                {student.enrollment.enrollment_no}
              </p>
            )}
          </>
        ) : (
          <span style={{ fontSize: '0.75rem', color: '#bbb', fontStyle: 'italic' }}>Not enrolled</span>
        )}
      </div>

      {/* Batch */}
      <div style={{ minWidth: 0 }}>
        {enrolled && student.enrollment.batch_name ? (
          <>
            <p style={{ margin: '0 0 2px 0', fontSize: '0.8rem', fontWeight: '600', color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {student.enrollment.batch_name}
            </p>
            {student.enrollment.batch_time && (
              <p style={{ margin: 0, fontSize: '0.7rem', color: '#666' }}>
                <i className="pi pi-clock" style={{ fontSize: '0.6rem', marginRight: '3px' }} />
                {student.enrollment.batch_time}
              </p>
            )}
          </>
        ) : (
          <span style={{ fontSize: '0.75rem', color: '#bbb', fontStyle: 'italic' }}>—</span>
        )}
      </div>

      {/* DOB */}
      <p style={{ margin: 0, fontSize: '0.78rem', color: '#555' }}>{formatDOB(student.dateOfBirth)}</p>

      {/* Gender */}
      <span style={{
        display: 'inline-block', padding: '3px 10px', borderRadius: '20px',
        fontSize: '0.7rem', fontWeight: '600', textTransform: 'capitalize',
        background: genderStyle.bg, color: genderStyle.color,
      }}>
        {student.gender ?? '—'}
      </span>

      {/* Status badges */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end' }}>
        <span style={{
          padding: '2px 8px', borderRadius: '12px', fontSize: '0.65rem', fontWeight: '600',
          background: student.status === 'active' ? '#d4f17e' : '#ffcdd2',
          color: student.status === 'active' ? '#0f3d3e' : '#b71c1c',
          textTransform: 'capitalize',
        }}>
          {student.status}
        </span>
        {student.is_registered && (
          <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '0.65rem', fontWeight: '600', background: '#e3f2fd', color: '#1565c0' }}>
            Portal
          </span>
        )}
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const BranchStudents: React.FC<BranchStudentsProps> = ({ branchId, instituteId, branchName, onBack }) => {
  const toast = useRef<Toast>(null);

  const [students, setStudents]       = useState<Student[]>([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [search, setSearch]           = useState('');
  const [page, setPage]               = useState(1);
  const [pagination, setPagination]   = useState<Pagination>({ page: 1, limit: 20, total: 0, pages: 0 });
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  // ── Debounce search ───────────────────────────────────────────────────────
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [debouncedSearch, statusFilter]);

  useEffect(() => { fetchStudents(); }, [page, debouncedSearch, statusFilter]);

  const fetchStudents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        branch_id: branchId,
        institute_id: instituteId,
        ...(debouncedSearch && { search: debouncedSearch }),
      });

      const res = await apiClient.get<{
        success: boolean;
        data: Student[];
        pagination: Pagination;
      }>(`/v1/students/get-students?${params}`);

      if (res.data.success) {
        // Client-side status filter since the API doesn't support it directly
        let filtered = res.data.data;
        if (statusFilter !== 'all') {
          filtered = filtered.filter((s) => s.status === statusFilter);
        }
        setStudents(filtered);
        setPagination(res.data.pagination);
      } else {
        setError('Failed to load students');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load students');
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load students', life: 3000 });
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, statusFilter]);

  const activeCount   = students.filter((s) => s.status === 'active').length;
  const enrolledCount = students.filter((s) => s.enrollment?.is_enrolled).length;
  const registeredCount = students.filter((s) => s.is_registered).length;

  return (
    <div style={{ padding: 0 }}>
      <Toast ref={toast} position="top-right" />

      {/* ── Hero ── */}
      <div style={{
        background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)',
        borderRadius: '20px', padding: '28px 32px', marginBottom: '24px', color: 'white',
        position: 'relative', overflow: 'hidden', boxShadow: '0 10px 40px rgba(15,61,62,0.3)',
      }}>
        <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '180px', height: '180px', background: 'rgba(212,241,126,0.08)', borderRadius: '50%', filter: 'blur(40px)' }} />

        <Button icon="pi pi-arrow-left" label="Back to Branch" onClick={onBack} text
          style={{ color: 'white', marginBottom: '10px', marginLeft: '-16px', fontSize: '0.9rem', position: 'relative', zIndex: 1 }} />

        <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <div style={{ display: 'inline-block', padding: '4px 12px', background: 'rgba(212,241,126,0.2)', borderRadius: '20px', marginBottom: '8px', fontSize: '0.68rem', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
              {branchName}
            </div>
            <h1 style={{ margin: '0 0 4px 0', fontSize: '1.6rem', fontWeight: '700', color: 'white', letterSpacing: '-0.5px' }}>Students</h1>
            <p style={{ margin: 0, color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>
              {pagination.total} student{pagination.total !== 1 ? 's' : ''} in this branch
            </p>
          </div>

          {/* Quick stats */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {[
              { label: 'Active',      value: activeCount,    icon: 'pi pi-check-circle' },
              { label: 'Enrolled',    value: enrolledCount,  icon: 'pi pi-book' },
              { label: 'Portal Access', value: registeredCount, icon: 'pi pi-user' },
            ].map((s) => (
              <div key={s.label} style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', padding: '10px 16px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.15)', textAlign: 'center', minWidth: '80px' }}>
                <i className={s.icon} style={{ fontSize: '0.8rem', color: '#d4f17e', display: 'block', marginBottom: '4px' }} />
                <p style={{ margin: '0 0 2px 0', fontSize: '1.2rem', fontWeight: '700', color: 'white' }}>{s.value}</p>
                <p style={{ margin: 0, fontSize: '0.62rem', color: 'rgba(255,255,255,0.65)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Filters ── */}
      <div style={{ background: 'white', borderRadius: '16px', padding: '16px 20px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        {/* Search */}
        <div style={{ position: 'relative', flex: 1, minWidth: '220px' }}>
          <i className="pi pi-search" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#aaa', fontSize: '0.85rem' }} />
          <InputText
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, mobile, email, Aadhaar..."
            style={{ width: '100%', paddingLeft: '36px', height: '40px', border: '1.5px solid #f0f0f0', borderRadius: '10px', fontSize: '0.85rem' }}
          />
        </div>

        {/* Status filter tabs */}
        <div style={{ display: 'flex', gap: '6px', background: '#f5f5f5', padding: '4px', borderRadius: '10px' }}>
          {(['all', 'active', 'inactive'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              style={{
                padding: '6px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                fontSize: '0.78rem', fontWeight: '600', textTransform: 'capitalize', transition: 'all 0.2s ease',
                background: statusFilter === s ? '#0f3d3e' : 'transparent',
                color: statusFilter === s ? 'white' : '#666',
              }}
            >
              {s}
            </button>
          ))}
        </div>

        {/* Result count */}
        {!loading && (
          <span style={{ fontSize: '0.78rem', color: '#999', whiteSpace: 'nowrap' }}>
            Showing {students.length} of {pagination.total}
          </span>
        )}
      </div>

      {error && <Message severity="error" text={error} style={{ marginBottom: '16px', fontSize: '0.85rem' }} />}

      {/* ── Table ── */}
      <div style={{ background: 'white', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0', marginBottom: '20px' }}>

        {/* Table header */}
        <div style={{
          display: 'grid', gridTemplateColumns: '48px 1fr 160px 160px 120px 100px 90px',
          gap: '16px', padding: '12px 20px',
          background: '#f8f9fa', borderBottom: '2px solid #f0f0f0',
        }}>
          {['', 'Student', 'Course', 'Batch', 'Date of Birth', 'Gender', 'Status'].map((h) => (
            <p key={h} style={{ margin: 0, fontSize: '0.68rem', fontWeight: '700', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</p>
          ))}
        </div>

        {/* Skeleton */}
        {loading && Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} />)}

        {/* Empty state */}
        {!loading && students.length === 0 && (
          <div style={{ padding: '60px 20px', textAlign: 'center' }}>
            <i className="pi pi-users" style={{ fontSize: '2.5rem', color: '#ddd', display: 'block', marginBottom: '12px' }} />
            <p style={{ margin: '0 0 6px 0', fontSize: '1rem', fontWeight: '600', color: '#666' }}>No students found</p>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#aaa' }}>
              {search ? 'Try adjusting your search or filters' : 'No students have been added to this branch yet'}
            </p>
          </div>
        )}

        {/* Rows */}
        {!loading && students.map((student, i) => (
          <StudentRow key={student.student_id} student={student} index={i} />
        ))}
      </div>

      {/* ── Pagination ── */}
      {pagination.pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '16px', background: 'white', borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0' }}>
          <button disabled={page === 1} onClick={() => setPage(1)}
            style={{ padding: '8px 12px', background: page === 1 ? '#f5f5f5' : '#0f3d3e', border: 'none', borderRadius: '8px', color: page === 1 ? '#bbb' : 'white', cursor: page === 1 ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '0.75rem' }}>
            <i className="pi pi-angle-double-left" />
          </button>
          <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}
            style={{ padding: '8px 12px', background: page === 1 ? '#f5f5f5' : '#0f3d3e', border: 'none', borderRadius: '8px', color: page === 1 ? '#bbb' : 'white', cursor: page === 1 ? 'not-allowed' : 'pointer', fontWeight: '600' }}>
            <i className="pi pi-chevron-left" />
          </button>

          {/* Page number pills */}
          {Array.from({ length: Math.min(pagination.pages, 5) }, (_, i) => {
            const start = Math.max(1, Math.min(page - 2, pagination.pages - 4));
            const p = start + i;
            return (
              <button key={p} onClick={() => setPage(p)}
                style={{ padding: '8px 14px', background: p === page ? '#0f3d3e' : '#f5f5f5', border: 'none', borderRadius: '8px', color: p === page ? 'white' : '#555', cursor: 'pointer', fontWeight: '700', fontSize: '0.85rem', minWidth: '36px' }}>
                {p}
              </button>
            );
          })}

          <button disabled={page === pagination.pages} onClick={() => setPage((p) => p + 1)}
            style={{ padding: '8px 12px', background: page === pagination.pages ? '#f5f5f5' : '#0f3d3e', border: 'none', borderRadius: '8px', color: page === pagination.pages ? '#bbb' : 'white', cursor: page === pagination.pages ? 'not-allowed' : 'pointer', fontWeight: '600' }}>
            <i className="pi pi-chevron-right" />
          </button>
          <button disabled={page === pagination.pages} onClick={() => setPage(pagination.pages)}
            style={{ padding: '8px 12px', background: page === pagination.pages ? '#f5f5f5' : '#0f3d3e', border: 'none', borderRadius: '8px', color: page === pagination.pages ? '#bbb' : 'white', cursor: page === pagination.pages ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '0.75rem' }}>
            <i className="pi pi-angle-double-right" />
          </button>
        </div>
      )}
    </div>
  );
};

export default BranchStudents;